import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { TerritoryDetail } from "@mapmycustomers/shared/types/entity/Territory";

import DataViewButton from "@app/component/DataView/DataViewButton";
import useMapView from "@app/component/view/utils/useMapView";
import messages from "@app/scene/map/components/TerritoriesMode/components/Sidebar/messages";
import TerritoryPolygon from "@app/scene/map/components/TerritoriesMode/components/TerritoryPolygon";
import useBoundsContext from "@app/scene/map/components/TerritoriesMode/utils/useBoundsContext";
import useTerritoryBoundsEditor from "@app/scene/map/components/TerritoriesMode/utils/useTerritoryBoundsEditor";
import {
  countPinsInTerritory,
  enterTerritoryMode,
  setDataViewData,
  toggleDataViewVisibility,
} from "@app/scene/map/store/territoryMode/actions";
import {
  getTerritoryPinsInBoundsCount,
  isTerritoryDataViewLoading,
} from "@app/scene/map/store/territoryMode/selectors";
import { RootState } from "@app/store/rootReducer";
import { isCreating } from "@app/store/territories";
import { createTerritory } from "@app/store/territories/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";

import BaseCreateTerritorySidebar from "../BaseCreateTerritorySidebar";

import styles from "./DrawTerritorySidebar.module.scss";
import TerritoryDrawer from "./TerritoryDrawer";

interface Props {
  dataViewLoading: boolean;
  loading?: boolean;
  onCreateTerritory?: typeof createTerritory.request;
  onEditTerritory: typeof enterTerritoryMode;
  onLoadPinCount: typeof countPinsInTerritory.request;
  onToggleDataViewVisibility: typeof toggleDataViewVisibility;
  pinsCount?: number;
  setDataViewData: typeof setDataViewData;
}

const DrawTerritorySidebar: React.FC<Props> = ({
  dataViewLoading,
  loading,
  onCreateTerritory,
  onEditTerritory,
  onLoadPinCount,
  onToggleDataViewVisibility,
  pinsCount,
  setDataViewData,
}) => {
  const { viewState } = useMapView();

  const intl = useIntl();
  const analytics = useExtendedAnalytics(["Create", "Draw"], useAnalytics());

  const [{ color, name, opacity, userIds }, setTerritoryInfo] = useState({
    color: Color.BLACK,
    // these initial values are actually ignored as they will be immediately replaced by the
    // default ones from the BaseCreateTerritorySidebar on onTerritoryChange callback
    name: "",
    opacity: 0.5,
    userIds: [],
  });

  const handleTerritoryFormChange = useCallback((name, color, opacity, userIds) => {
    setTerritoryInfo({ color, name, opacity, userIds });
  }, []);

  const { canRedo, canUndo, changeVertex, moveTerritory, paths, redo, reset, undo } =
    useTerritoryBoundsEditor([]);

  // start over counter is a hacky way to reset territory drawer
  const [startOverCounter, setStartOverCounter] = useState(0);
  const handleStartOver = useCallback(() => {
    setStartOverCounter((value) => value + 1);
    reset([]);
  }, [reset]);

  const { setCanChangeState, setCanRedo, setCanUndo, setListeners } = useBoundsContext();
  useEffect(() => setCanChangeState(!paths.length), [paths, setCanChangeState]);
  useEffect(
    () => setListeners({ onRedo: redo, onStartOver: handleStartOver, onUndo: undo }),
    [handleStartOver, redo, setListeners, undo]
  );
  useEffect(() => setCanUndo(canUndo), [canUndo, setCanUndo]);
  useEffect(() => setCanRedo(canRedo), [canRedo, setCanRedo]);

  // render territory dynamically
  const editedTerritory = useMemo(
    () => ({
      color: color ?? null,
      territoryDetail: {
        hidden: false,
        opacity,
        points: { coordinates: paths, type: "Polygon" } as TerritoryDetail["points"],
      },
    }),
    [color, opacity, paths]
  );

  const nameIsValid = name.trim().length > 0;
  const pathIsValid = paths.length > 0;
  const canSave = nameIsValid && pathIsValid;

  const handleSave = useCallback(() => {
    if (!canSave) {
      return;
    }
    onCreateTerritory?.({
      onSuccess: (territory: Territory) => {
        onEditTerritory({ territory, territoryId: territory.id });
        analytics.completed("", { sharingWithUsers: userIds.length > 0 });
      },
      territory: {
        adm1: [],
        adm2: [],
        adm3: [],
        color,
        coordinates: paths,
        name,
        opacity,
        type: "points",
      },
      userIdsToShareWith: userIds,
    });
  }, [
    analytics,
    canSave,
    color,
    name,
    onCreateTerritory,
    onEditTerritory,
    opacity,
    paths,
    userIds,
  ]);

  useEffect(() => {
    onLoadPinCount(paths);
  }, [
    onLoadPinCount,
    paths,
    // all deps are correct here and visibleEntities is not a mistake - we really
    // need to recalculate pin count when entity visibilities change
    // And the magic with MAP_ENTITY_TYPES.map is needed to keep number of deps
    // constant even if visibleEntities length changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...MAP_ENTITY_TYPES.map((entityType) => viewState.visibleEntities.includes(entityType)),
  ]);

  useEffect(() => {
    setDataViewData(paths);
  }, [paths, setDataViewData]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      <BaseCreateTerritorySidebar
        count={pinsCount}
        creating={loading}
        onSave={handleSave}
        onTerritoryChanged={handleTerritoryFormChange}
        preventClicks
        saveDisabled={
          (!nameIsValid
            ? intl.formatMessage(messages.emptyName)
            : !pathIsValid
            ? intl.formatMessage(messages.nothingDrawn)
            : false) || loading
        }
        shouldShowPrompt={!loading && paths.length > 0}
      >
        {paths.length > 0 ? (
          <>
            <TerritoryPolygon
              onChangeVertex={changeVertex}
              onMoveTerritory={moveTerritory}
              territory={editedTerritory}
            />
            <div className={styles.dataViewContainer}>
              <DataViewButton
                entityCount={pinsCount ?? 0}
                loading={dataViewLoading}
                onClick={onToggleDataViewVisibility}
              />
            </div>
          </>
        ) : (
          <TerritoryDrawer
            color={color}
            key={startOverCounter} // a hacky way to reset territory drawer
            onCreatePolygon={reset}
            opacity={opacity}
          />
        )}
      </BaseCreateTerritorySidebar>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  dataViewLoading: isTerritoryDataViewLoading(state),
  loading: isCreating(state),
  pinsCount: getTerritoryPinsInBoundsCount(state),
});

const mapDispatchToProps = {
  onCreateTerritory: createTerritory.request,
  onEditTerritory: enterTerritoryMode,
  onLoadPinCount: countPinsInTerritory.request,
  onToggleDataViewVisibility: toggleDataViewVisibility,
  setDataViewData,
};

export default connect(mapStateToProps, mapDispatchToProps)(DrawTerritorySidebar);
